"use client";

import Link from "next/link";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useInvoiceStore } from "@/lib/invoice/store";
import { compareText, computeTotals, docSign, fmtMoney, invoiceStatus, isBillable } from "@/lib/invoice/utils";
import { Button, Empty, Input, Loading, Modal, PageHeader, useFeedback } from "@/components/Invoice/ui";
import { CLIENT_BLANK as BLANK, ClientForm } from "@/components/Invoice/ClientForm";

function ClientsInner() {
  const store = useInvoiceStore();
  const params = useSearchParams();
  const { toast, confirm } = useFeedback();
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (!store.ready) return;
    if (params.get("new")) setEditing({ ...BLANK });
    const open = store.clients.find((c) => c.id === params.get("open"));
    if (open) setEditing({ ...BLANK, ...open });
  }, [store.ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    const m = {};
    for (const inv of store.invoices) {
      if (!isBillable(inv)) continue;
      const t = computeTotals(inv);
      const s = invoiceStatus(inv, t);
      if (s === "cancelled" || s === "draft") continue;
      const e = (m[inv.clientId] ||= { count: 0, last: "", byCur: {} });
      e.count++;
      if (inv.date > e.last) e.last = inv.date;
      const c = (e.byCur[inv.currency] ||= { total: 0, due: 0 });
      c.total += docSign(inv) * t.total;
      c.due += docSign(inv) * Math.max(0, t.balance);
    }
    return m;
  }, [store.invoices]);

  if (!store.ready) return <Loading />;

  const archivedCount = store.clients.filter((c) => c.archived).length;
  const list = store.clients
    .filter((c) => !!c.archived === showArchived)
    .filter((c) => [c.name, c.email, c.contactPerson, c.gstin, c.city].join(" ").toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => compareText(a.name, b.name));

  const save = () => {
    if (!editing.name.trim()) return toast("Client name is required", { tone: "error" });
    const dup = store.clients.find((c) => c.id !== editing.id && c.name.trim().toLowerCase() === editing.name.trim().toLowerCase());
    if (dup) toast(`Note: another client is also called “${dup.name}”.`);
    store.saveClient({ ...editing, name: editing.name.trim() });
    setEditing(null);
    toast("Client saved", { tone: "success" });
  };

  const remove = async (c) => {
    const invCount = store.invoices.filter((i) => i.clientId === c.id).length;
    const projCount = store.projects.filter((p) => p.clientId === c.id).length;
    const ok = await confirm(
      <>
        Delete <b>{c.name}</b> and its {projCount} project(s)?
        {invCount > 0 && <> Its {invCount} invoice(s) are kept. Tip: <b>Archive</b> instead to hide a client you no longer work with.</>}
        <div className="mt-2 text-slate-500">You can restore it from Settings → Recycle bin for 30 days.</div>
      </>,
      { title: "Delete client", okLabel: "Delete", danger: true }
    );
    if (!ok) return;
    const tid = store.deleteClient(c.id);
    setEditing(null);
    toast(`${c.name} moved to the recycle bin`, { action: { label: "Undo", onClick: () => store.restoreTrash(tid) } });
  };

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle={`${store.clients.length - archivedCount} active${archivedCount ? ` · ${archivedCount} archived` : ""}`}
        actions={<Button onClick={() => setEditing({ ...BLANK })}>+ Add client</Button>}
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input className="max-w-sm" placeholder="Search clients…" value={q} onChange={(e) => setQ(e.target.value)} />
        {archivedCount > 0 && (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived
          </label>
        )}
      </div>

      {list.length === 0 ? (
        <Empty title={showArchived ? "No archived clients" : "No clients yet"}>
          Add your first client, or import your August 2026 client & projects from the Dashboard.
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200/80 bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Projects</th>
                <th className="px-4 py-3">Invoiced</th>
                <th className="px-4 py-3">Outstanding</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((c) => {
                const s = stats[c.id];
                const active = store.projects.filter((p) => p.clientId === c.id && p.status === "active").length;
                const total = store.projects.filter((p) => p.clientId === c.id).length;
                return (
                  <tr key={c.id} className="transition-colors hover:bg-slate-50/70">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {c.logo ? (
                          <img src={c.logo} alt="" className="h-9 w-9 rounded border border-slate-200 bg-white object-contain" />
                        ) : (
                          <div className="flex h-9 w-9 items-center justify-center rounded bg-slate-100 text-xs font-bold text-slate-500">
                            {c.name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
                          </div>
                        )}
                        <div>
                          <button className="font-semibold hover:text-brand-700" onClick={() => setEditing({ ...BLANK, ...c })}>
                            {c.name}
                          </button>
                          <div className="text-xs text-slate-500">
                            {[c.city, c.country].filter(Boolean).join(", ")}
                            {c.gstin ? ` · ${c.gstin}` : ""}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <div>{c.contactPerson}</div>
                      <div className="text-xs">{c.email || c.phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Link className="text-brand-700 hover:underline" href={`/invoice/projects?client=${c.id}`}>
                        {active} active / {total}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      {s ? Object.entries(s.byCur).map(([cur, v]) => <div key={cur}>{fmtMoney(v.total, cur)}</div>) : "—"}
                      {s?.last && <div className="text-[11px] text-slate-400">Last: {s.last}</div>}
                    </td>
                    <td className="px-4 py-3 font-medium text-brand-700">
                      {s ? Object.entries(s.byCur).filter(([, v]) => v.due > 0).map(([cur, v]) => <div key={cur}>{fmtMoney(v.due, cur)}</div>) : ""}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <Link href={`/invoice/invoices/edit?client=${c.id}`} className="mr-3 text-xs font-medium text-brand-700 hover:underline">
                        New invoice
                      </Link>
                      <Link href={`/invoice/clients/statement?id=${c.id}`} className="mr-3 text-xs text-slate-600 hover:underline">
                        Statement
                      </Link>
                      <Link href={`/invoice/invoices?client=${c.id}`} className="mr-3 text-xs text-slate-600 hover:underline">
                        Invoices
                      </Link>
                      <Button size="sm" variant="secondary" onClick={() => setEditing({ ...BLANK, ...c })}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit client" : "New client"}
        wide
        footer={
          <>
            {editing?.id && (
              <div className="mr-auto flex gap-2">
                <Button variant="danger" onClick={() => remove(editing)}>Delete</Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    store.setClientArchived(editing.id, !editing.archived);
                    toast(editing.archived ? "Client restored from archive" : "Client archived – hidden from lists and billing runs");
                    setEditing(null);
                  }}
                >
                  {editing.archived ? "Unarchive" : "Archive"}
                </Button>
              </div>
            )}
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save}>Save client</Button>
          </>
        }
      >
        {editing && <ClientForm value={editing} onChange={setEditing} brands={store.brands} />}
      </Modal>
    </div>
  );
}

export default function ClientsPage() {
  return (
    <Suspense fallback={<Loading />}>
      <ClientsInner />
    </Suspense>
  );
}
